import { Button } from "./ui/button.jsx";
import React, { useState, useEffect } from "react";
import { PaymentBadges } from "./paymentbadges.tsx";
import { money } from "../utilities/currency.js";
import axios from "axios";
import Swal from "sweetalert2";
import VippsAuthenticate from "./ui/vippsauthenticate.tsx";
import StripeAuthenticate from "./ui/stripeauthenticate.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../state/store.ts";

function LeftColumn({ t, cc, setCc, phone, setPhone, amount, setAmount, presets, paymentMethod, setPaymentMethod, error, handlePurchase }: { t: any, cc: string, setCc: (cc: string) => void, phone: string, setPhone: (phone: string) => void, amount: number, setAmount: (amt: number) => void, presets: number[], paymentMethod: string, setPaymentMethod: (method: string) => void, error: string, handlePurchase: () => void }) {
  return (
    <div className="min-w-0">
      <h3 className="text-2xl font-bold text-[var(--primary-color)] mb-4">Kjøp ringeminutter</h3>
      <p className="mb-4">Bruk dem på hvilken som helst tjeneste og veileder</p>

      <div className="bg-white rounded-2xl shadow-lg p-6 border border-[var(--primary-color)] w-full max-w-full">
        {/* Phone */}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t.numLabel}
        </label>
        <div className="flex gap-2 min-w-0">
          <select
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            className="rounded-xl border-[var(--primary-color)] focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] px-3 py-2"
          >
            <option value="+47">+47 🇳🇴</option>
          </select>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="Ditt mobilnummer"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="flex-1 min-w-0 rounded-xl border-[var(--primary-color)] focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] px-4 py-2"
          />
        </div>

        {/* Amount */}
        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
          {t.amount}
        </label>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => setAmount(p)}
              className={`px-3 py-2 rounded-full border text-sm ${amount === p
                ? "bg-[var(--primary-color)] text-white border-[var(--primary-color)]"
                : "bg-white text-[var(--primary-color)] border-[var(--primary-color)] hover:border-[var(--primary-color)]"
                }`}
            >
              {money(p)}
            </button>
          ))}
        </div>

        {/* Payment methods */}
        <div className="mt-4">
          <PaymentBadges selected={paymentMethod} onSelect={setPaymentMethod} />
        </div>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <button
          onClick={handlePurchase}
          className="mt-4 py-2 w-full bg-[var(--primary-color)] hover:bg-[var(--primary-color)] text-white rounded-full"
        >
          {t.buy(amount)}
        </button>
        <p className="mt-3 text-xs text-gray-500">{t.learn}</p>
      </div>
    </div >
  )
}
function RightColumn({ wallet, from, successMsg, handleImageChange }: { wallet: any, from: string, successMsg: string, handleImageChange: (e: any) => void }) {
  return (
    <div className="min-w-0">
      <img
        src={wallet?.image}
        alt="wallet"
        className="w-full max-w-full h-auto max-h-[441px] rounded-2xl shadow-lg"
      />
      {from === "support-client" && (
        <input type="file" accept="image/png, image/jpeg" className="w-full mt-1" onChange={handleImageChange} />
      )}
      {successMsg && (
        <p className="mt-3 text-sm text-green-700">{successMsg}</p>
      )}
    </div>
  )
}

export function BuyMinutesSection({ onTopUp, T, baseUrl, from, setHeroData, wallet }: { onTopUp: (amt: number, method: string) => void, T: any, baseUrl: string, from: string, setHeroData?: (data: any) => void, wallet: any }) {
  const [vippsAuthenticate, setVippsAuthenticate] = useState(false);
  const [cc, setCc] = useState("+47");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(399);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("vipps"); // default

  const [stripeAuthenticate, setStripeAuthenticate] = useState(false);
  // const client_uid = 'clnt_QI9Jd1m0';
  const client = useSelector((state: RootState) => state.client.client);
  const client_uid = client?.uid || "clnt_QI9Jd1m0"; // fallback to default if not available

  const presets = [149, 399, 999];
  const validPhone = /\d{7,13}/.test(phone.replace(/\D/g, ""));
  const t = T.wallet;

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "WALLET_TOPUP") {
        Swal.fire({
          icon: "success",
          title: "Betaling godkjent",
          text: "Takk for at betalingen din på " + event.data.amount + " er fullført.",
          showConfirmButton: true,
        });
        onTopUp(event.data.amount, event.data.method);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onTopUp]);

  function handlePurchase() {
    setSuccessMsg("");
    if (!validPhone) {
      setError(t.err);
      return;
    }
    setError("");
    // send an axios request to endpoint and pass all data amount, paymentMethod, phone
    (async () => {
      try {
        var ep = "";
        if (paymentMethod === "vipps") {
          ep = baseUrl + "/buyWalletCreditVipps";
          setVippsAuthenticate(true);
        } else if (paymentMethod === "card") {
          ep = baseUrl + "/buyWalletCreditCard";
          setStripeAuthenticate(true);
        }
        var formData = new FormData();
        formData.append("amount", amount.toString());
        formData.append("paymentMethod", paymentMethod);
        formData.append("client_uid", client_uid);
        formData.append("phone", phone);
        formData.append("country", cc);
        const res = await axios.post(ep, formData);

        console.log(res.data);

        if (paymentMethod === "vipps" && res.data.status === "OK") {
          const { status, reference, access_token, endpoint, customer_uid } = res.data;
          var formData1 = new FormData();
          formData1.append("customer_uid", customer_uid);
          formData1.append("access_token", access_token);
          formData1.append("reference", reference);
          formData1.append("endpoint", endpoint);
          formData1.append("client_uid", client_uid);

          const pollAuth = async (intervalId: ReturnType<typeof setInterval>) => {
            try {
              const secondRes = await axios.post(baseUrl + "/authenticateBuyWalletCreditVipps", formData1);

              console.log("Polling result:", secondRes.data);

              if (secondRes.data.status && secondRes.data.status === "OK") {
                onTopUp(amount, paymentMethod);
                setVippsAuthenticate(false);
                clearInterval(intervalId); // ✅ stop polling
                Swal.fire({
                  icon: "success",
                  title: "Betaling godkjent",
                  text: "Takk for at betalingen din på " + amount + " er fullført.",
                  showConfirmButton: true,
                });
                // setSuccessMsg(t.success(amount) || "Payment authorized!");
              } else if (secondRes.data.status && secondRes.data.status === "ABORTED") {
                setVippsAuthenticate(false);
                clearInterval(intervalId); // ✅ stop polling
                Swal.fire({
                  icon: "error",
                  title: "Betaling avbrutt",
                  text: "Du har avbrutt betalingen.",
                  showConfirmButton: true,
                });
              }
            } catch (err) {
              clearInterval(intervalId); // stop polling on error
              setError(t.apiErr || "Noe gikk galt. Prøv igjen.");
            }
          };

          // Start polling every 2s
          const intervalId = setInterval(() => pollAuth(intervalId), 2000);
        } else if (paymentMethod === "card" && res.data.status === "OK") {
          setStripeAuthenticate(false);
          window.open(res.data.url);
        }
      } catch (err) {
        setError(t.apiErr || "Noe gikk galt. Prøv igjen.");
        return;
      }
    })();

    // setShowOtp(true);
  }

  function handleVerify() {
    if (!/^\d{4,6}$/.test(otp)) {
      setError("Ugyldig kode");
      return;
    }
    setError("");
    setShowOtp(false);
    setOtp("");
    if (typeof onTopUp === "function") onTopUp(amount, paymentMethod); // ✅ pass both
    setSuccessMsg(t.success(amount));
  }

  function handleImageChange(e: any) {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    if (setHeroData) {
      setHeroData(prev => ({
        ...prev,
        wallet: {
          ...prev.wallet,
          image: previewUrl, // 🔥 instant preview
          file,              // keep file for backend upload
        },
      }));
    }
  }

  if (vippsAuthenticate) {
    return <VippsAuthenticate vippsAuthenticate={vippsAuthenticate} />
  }

  if (stripeAuthenticate) {
    return <StripeAuthenticate stripeAuthenticate={stripeAuthenticate} />
  }

  return (
    <section className="px-4 py-10 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-indigo-50 border-y border-[var(--primary-color)]/50">

      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center">
        {wallet?.order === "left" ? (
          <>
            <RightColumn wallet={wallet} from={from} successMsg={successMsg} handleImageChange={handleImageChange} />
            <LeftColumn t={t} cc={cc} setCc={setCc} phone={phone} setPhone={setPhone} amount={amount} setAmount={setAmount} presets={presets} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} error={error} handlePurchase={handlePurchase} />
          </>
        ) : (
          <>
            <LeftColumn t={t} cc={cc} setCc={setCc} phone={phone} setPhone={setPhone} amount={amount} setAmount={setAmount} presets={presets} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} error={error} handlePurchase={handlePurchase} />
            <RightColumn wallet={wallet} from={from} successMsg={successMsg} handleImageChange={handleImageChange} />
          </>
        )}
      </div>
      {from === "support-client" && (
        <div className="flex gap-2 mt-2 justify-center">
          <button
            onClick={() =>
              setHeroData(prev => ({
                ...prev,
                wallet: { ...prev.wallet, order: "left" },
              }))
            }
            className={`px-2 py-1 rounded border`}
          >
            Move Left
          </button>

          <button
            onClick={() =>
              setHeroData(prev => ({
                ...prev,
                wallet: { ...prev.wallet, order: "right" },
              }))
            }
            className="px-2 py-1 rounded border"
          >
            Move Right
          </button>
        </div>
      )}

      {/* OTP Modal */}
      {showOtp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowOtp(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl p-6 w-[85%] max-w-md border border-purple-100">
            <h3 className="text-lg font-bold text-purple-900 mb-2">{t.enterOtp}</h3>
            <p className="text-sm text-gray-600 mb-4">{t.otp(`${cc} ${phone}`)}</p>
            <input
              autoFocus
              type="tel"
              inputMode="numeric"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="••••"
              className="w-full rounded-xl border-gray-300 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] px-4 py-2"
            />
            <div className="flex gap-3 justify-end mt-4">
              <Button variant="outline" onClick={() => setShowOtp(false)} className="rounded-full border-[var(--primary-color)]/50">
                {t.cancel}
              </Button>
              <Button onClick={handleVerify} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white">
                {t.verify}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
